/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { FavoriteActionMode } from '../../configuration/favorites';
import { ErrorCode } from '../../modules/order-details';
import { ensureAuthenticated } from '../../utilities/is-authenticated';
import { IOrderFavorite } from './get-order-favorites.action';

/**
 * PostOrderFavorites Input Action
 */
export class PostOrderFavoritesInput implements Msdyn365.IActionInput {
    public orderNumber: string;
    public title: string;
    public mode: FavoriteActionMode;

    constructor(orderNumber: string, title: string, mode: FavoriteActionMode) {
        this.orderNumber = orderNumber;
        this.title = title;
        this.mode = mode;
    }

    public getCacheKey = () => `PostOrderFavorites-${this.orderNumber}`;
    public getCacheObjectType = () => 'PostOrderFavorites';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

export interface IPostOrderFavoritesData {
    '@odata.context'?: string;
    Success: boolean;
    Favorites: IOrderFavorite[];
    error?: ErrorCode;
}

const createInput = (_: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new PostOrderFavoritesInput('', '', FavoriteActionMode.Add);
};

export async function action(input: PostOrderFavoritesInput, ctx: Msdyn365.IActionContext): Promise<IPostOrderFavoritesData> {
    ensureAuthenticated(ctx);

    const response = await Msdyn365.sendRequest<IPostOrderFavoritesData>(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/UpdateFavoriteOrders`,
        'post',
        { orderNumber: input.orderNumber, title: input.title, mode: input.mode },
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${ctx.requestContext.user.token}`
            }
        }
    );

    if (response.status === 200 && response.data) {
        return { ...response.data };
    }

    return { Success: false, Favorites: [], error: { errorCode: response.status, errorText: response.statusText } };
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IPostOrderFavoritesData>>action,
    id: 'PostOrderFavorites',
    input: createInput
});
